import {
  BarChart3,
  CalendarClock,
  ClipboardList,
  Coins,
  CreditCard,
  FileText,
  KeyRound,
  LayoutDashboard,
  LockOpen,
  MessagesSquare,
  Package,
  Repeat,
  ShieldCheck,
  Sparkles,
  UserRound,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';

/**
 * Plan de navigation du back-office (cahier des charges §4, schéma « Six
 * domaines ») : une seule source pour la barre latérale, la recherche Cmd+K
 * et les routes.
 */
export interface NavEntry {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** Lot du plan de livraison qui construit l'écran. */
  lot: 1 | 2 | 3;
  /** Section du cahier des charges. */
  section: string;
  /** Ce que l'écran permettra, en une phrase. */
  summary: string;
  superAdminOnly?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  entries: NavEntry[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'pilotage',
    label: 'Pilotage',
    entries: [
      { id: 'dashboard', label: 'Tableau de bord', path: '/', icon: LayoutDashboard, lot: 1, section: '7.1', summary: 'Chiffres clés, points à traiter et raccourcis.' },
      { id: 'analytics', label: 'Analytics', path: '/analytics', icon: BarChart3, lot: 3, section: '7.10', summary: 'Usage par jour, par coach, par module et par cohorte.' },
    ],
  },
  {
    id: 'eleves',
    label: 'Élèves',
    entries: [
      { id: 'students', label: 'Élèves', path: '/eleves', icon: UsersRound, lot: 1, section: '7.2', summary: 'Retrouver une élève et traiter sa demande depuis sa fiche.' },
      { id: 'credits', label: 'Crédits', path: '/credits', icon: Coins, lot: 1, section: '7.3', summary: 'Journal global, ajout groupé et prix des générations (barème).' },
      { id: 'conversations', label: 'Conversations, mémoires', path: '/conversations', icon: MessagesSquare, lot: 3, section: '7.9', summary: 'Consultation tracée pour le support, demandes RGPD.' },
    ],
  },
  {
    id: 'acces',
    label: 'Accès',
    entries: [
      { id: 'access-keys', label: 'Clés d’accès', path: '/cles-acces', icon: KeyRound, lot: 1, section: '7.4', summary: 'Créer des clés par type et suivre leurs utilisations.' },
      { id: 'unlock-keys', label: 'Clés de déblocage', path: '/cles-deblocage', icon: LockOpen, lot: 1, section: '7.5', summary: 'Clés qui ouvrent un coach à une élève inscrite.' },
      { id: 'events', label: 'Événements', path: '/evenements', icon: CalendarClock, lot: 2, section: '7.6', summary: 'Calendrier des exercices, prévisualisation, fin d’événement.' },
    ],
  },
  {
    id: 'contenus',
    label: 'Contenus',
    entries: [
      { id: 'coaches', label: 'Modules et coachs', path: '/coachs', icon: Sparkles, lot: 3, section: '7.7', summary: 'Organisation du catalogue et réglages de chaque coach.' },
      { id: 'prompts', label: 'Prompts des coachs', path: '/prompts', icon: FileText, lot: 3, section: '7.7', summary: 'Versions, comparaison et test avant publication.' },
    ],
  },
  {
    id: 'ventes',
    label: 'Ventes',
    entries: [
      { id: 'packs', label: 'Packs (ponctuel)', path: '/ventes/packs', icon: Package, lot: 2, section: '7.8', summary: 'Packs de crédits achetés en une fois.' },
      { id: 'plans', label: 'Abonnements (récurrent)', path: '/ventes/abonnements', icon: Repeat, lot: 2, section: '7.8', summary: 'Formules mensuelles ou annuelles.' },
      { id: 'subscribers', label: 'Abonnées', path: '/ventes/abonnees', icon: UserRound, lot: 2, section: '7.8', summary: 'Abonnements en cours et résiliations programmées.' },
      { id: 'payments', label: 'Paiements', path: '/ventes/paiements', icon: CreditCard, lot: 2, section: '7.8', summary: 'Paiements, remboursements, export comptable.' },
    ],
  },
  {
    id: 'systeme',
    label: 'Système',
    entries: [
      { id: 'admins', label: 'Administrateurs', path: '/administrateurs', icon: ShieldCheck, lot: 3, section: '7.11', summary: 'Comptes admin, rôles et invitations.', superAdminOnly: true },
      { id: 'audit', label: 'Journal d’audit', path: '/journal', icon: ClipboardList, lot: 3, section: '7.12', summary: 'Qui a fait quoi, quand, sur quel objet.' },
    ],
  },
];

export const ALL_ENTRIES: NavEntry[] = NAV_GROUPS.flatMap((g) => g.entries);

/**
 * Écrans sans entrée dans la barre latérale (un onglet d'un écran), trouvés
 * par la recherche Cmd+K : par leur nom, leur description ou ces mots-clés.
 */
export const RACCOURCIS_RECHERCHE: Array<NavEntry & { motsCles: string }> = [
  {
    id: 'prix-generations',
    label: 'Prix des générations',
    path: '/credits/bareme',
    icon: Coins,
    lot: 1,
    section: '7.3',
    summary: 'Coût en crédits d’une image, d’une image HD, d’un moodboard, d’un logo, d’une palette et d’une seconde de vidéo.',
    motsCles: 'prix tarif cout bareme generation photo image hd video moodboard logo palette credits',
  },
];

/** Groupes et entrées visibles pour un rôle : un menu interdit n'est pas affiché. */
export function visibleGroups(isSuperAdmin: boolean): NavGroup[] {
  return NAV_GROUPS.map((g) => ({ ...g, entries: g.entries.filter((e) => isSuperAdmin || !e.superAdminOnly) })).filter(
    (g) => g.entries.length > 0
  );
}

/** Minuscules sans accents, pour chercher « cles » comme « Clés ». */
export function normaliser(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/’/g, "'").toLowerCase().trim();
}

/**
 * Écrans correspondant à la recherche : d'abord ceux dont le nom la contient,
 * puis ceux dont seule la description la contient, chacun dans l'ordre du menu.
 */
export function chercherEcrans(requete: string, isSuperAdmin: boolean): NavEntry[] {
  const q = normaliser(requete);
  const visibles = visibleGroups(isSuperAdmin).flatMap((g) => g.entries);
  if (!q) return visibles;
  const raccourcis = RACCOURCIS_RECHERCHE.filter((r) => isSuperAdmin || !r.superAdminOnly);
  const parNom = [...raccourcis, ...visibles].filter((e) => normaliser(e.label).includes(q));
  const parMotCle = raccourcis.filter((r) => !parNom.includes(r) && normaliser(r.motsCles).split(' ').some((m) => m.startsWith(q)));
  const parDescription = [...visibles, ...raccourcis].filter(
    (e) => !parNom.includes(e) && !parMotCle.includes(e as (typeof raccourcis)[number]) && normaliser(e.summary).includes(q)
  );
  return [...parNom, ...parMotCle, ...parDescription];
}
